import type { Coach } from "./mockCoaches";
import { mockCoaches } from "./mockCoaches";

type LessonTypeId = "private" | "group";

type BookingLessonType = {
  id: LessonTypeId;
  label: string;
  price: string;
  unit: string;
  description: string;
  duration: string;
  tagline: string;
  bullets: string[];
};

export type GroupParticipant = {
  id: string;
  name: string;
  skillLevel?: string;
  focusArea?: string;
  avatarUrl?: string;
  joinedLabel?: string;
};

type BookingSlot = {
  id: string;
  time: string;
  lessonType: LessonTypeId;
  duration: string;
  price: string;
  spotsRemaining: number;
  title?: string;
  participants?: GroupParticipant[];
  location?: string;
};

type BookingDate = {
  id: string;
  day: string;
  date: string;
  label: string;
  totalSlots: number;
  slots: BookingSlot[];
};

type HighlightChipIcon = "users" | "trophy" | "spark";

type HighlightChip = {
  icon: HighlightChipIcon;
  label: string;
};

type MetricIcon = "dollar" | "users" | "clock" | "map";

type Metric = {
  icon: MetricIcon;
  label: string;
  value: string;
  caption?: string;
};

type LessonDetail = {
  title: string;
  description: string;
  price: string;
  cadence: string;
};

type LessonPackage = {
  lessonTypeId: LessonTypeId;
  id: string;
  title: string;
  lessons: number;
  discount: string;
  description: string;
  totalPrice: string;
  pricePerLesson: string;
};

type PlayerLessonCredit = {
  lessonTypeId: LessonTypeId;
  lessonTypeLabel: string;
  remaining: number;
  totalPurchased?: number;
  upcomingExpiryLabel?: string;
  lastPurchasedLabel?: string;
};

export type CoachProfile = Coach & {
  about: string;
  headlineBadge?: string;
  highlightChips: HighlightChip[];
  metrics: Metric[];
  certifications: string[];
  specialties: string[];
  coachingLocations: string[];
  lessonDetails: LessonDetail[];
  lessonPackages: LessonPackage[];
  playerLessonCredits?: PlayerLessonCredit[];
  booking: {
    headline: string;
    lessonTypes: BookingLessonType[];
    defaultLessonType: LessonTypeId;
    monthLabel: string;
    availableDates: BookingDate[];
    note: string;
  };
};

const sharedProfileBase = {
  summary:
    "Former college player with 10+ years coaching experience. Specializes in serve technique, match strategy, and high-performance mindset work. Helping players of all levels unlock sustainable progress with data-led plans and targeted drills. Her approach focuses on building confident strokes, dynamic movement, and tactical awareness under pressure.",
  highlightChips: [
    { icon: "users" as const, label: "85 students" },
    { icon: "trophy" as const, label: "10+ years coaching" },
    { icon: "spark" as const, label: "UTR 9.8" },
  ],
  metrics: [
    { icon: "dollar" as const, label: "Private", value: "$85", caption: "per session" },
    { icon: "users" as const, label: "Group", value: "$50", caption: "per player" },
    { icon: "clock" as const, label: "Response time", value: "< 1 hour" },
    { icon: "map" as const, label: "Locations", value: "3" },
  ],
  certifications: ["USPTA Certified", "PTR Professional", "SafeSport Certified"],
  specialties: ["Serve technique", "Match strategy", "Tournament prep"],
  coachingLocations: ["Oceanside Tennis Center", "Vista Courts", "Cartland Tennis Club"],
  lessonDetails: [
    {
      title: "One-on-one session",
      description: "Intensive 60-minute session tailored to your goals with video breakdowns.",
      price: "$85",
      cadence: "per hour",
    },
    {
      title: "Group session (2-4 players)",
      description: "Sharpen live-ball decision making and coordinated drills for small groups.",
      price: "$50",
      cadence: "per player",
    },
  ],
  lessonPackages: [
    {
      lessonTypeId: "private" as const,
      id: "private-5-pack",
      title: "5 lesson package",
      lessons: 5,
      discount: "Save 10%",
      description: "Secure five private sessions up front with flexible rescheduling.",
      totalPrice: "$382.50",
      pricePerLesson: "$76.50 per lesson",
    },
    {
      lessonTypeId: "private" as const,
      id: "private-10-pack",
      title: "10 lesson package",
      lessons: 10,
      discount: "Save 15%",
      description: "Lock in a season of progress and priority court access.",
      totalPrice: "$722.50",
      pricePerLesson: "$72.25 per lesson",
    },
    {
      lessonTypeId: "group" as const,
      id: "group-5-pack",
      title: "5 session package",
      lessons: 5,
      discount: "Save 10%",
      description: "Reserve a block of high-energy squad trainings for your crew.",
      totalPrice: "$225.00",
      pricePerLesson: "$45.00 per player",
    },
    {
      lessonTypeId: "group" as const,
      id: "group-10-pack",
      title: "10 session package",
      lessons: 10,
      discount: "Save 15%",
      description: "Lock in weekly group reps with match-play add-ons included.",
      totalPrice: "$425.00",
      pricePerLesson: "$42.50 per player",
    },
  ],
  playerLessonCredits: [
    {
      lessonTypeId: "private" as const,
      lessonTypeLabel: "Private lessons",
      remaining: 2,
      totalPurchased: 5,
      upcomingExpiryLabel: "2 credits expire May 31",
      lastPurchasedLabel: "Purchased April 12",
    },
    {
      lessonTypeId: "group" as const,
      lessonTypeLabel: "Group sessions",
      remaining: 1,
      totalPurchased: 3,
      upcomingExpiryLabel: "1 credit expires June 18",
    },
  ],
  booking: {
    headline: "Book a Lesson",
    lessonTypes: [
      {
        id: "private" as const,
        label: "Private lesson",
        price: "$85",
        unit: "/ hour",
        description: "Laser-focused individual instruction",
        duration: "60 min • 1 player",
        tagline: "Dial in technique with video feedback",
        bullets: [
          "Custom drills + footwork",
          "Live ball + situational points",
          "Next-step practice plan recap",
        ],
      },
      {
        id: "group" as const,
        label: "Group session",
        price: "$50",
        unit: "/ player",
        description: "High-energy squad training",
        duration: "75 min • 2-4 players",
        tagline: "Sharpen together with competitive reps",
        bullets: [
          "Serve + return patterns",
          "Live point play rotations",
          "Shared match video breakdown",
        ],
      },
    ],
    defaultLessonType: "private" as const,
    monthLabel: "October 2025",
    availableDates: [
      {
        id: "2025-10-31",
        day: "Fri",
        date: "31",
        label: "Oct 31",
        totalSlots: 16,
        slots: [
          {
            id: "2025-10-31-private-0730",
            time: "7:30 AM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-10-31-group-0900",
            time: "9:00 AM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 3,
            title: "Live Ball Clinic",
            participants: [
              {
                id: "amy-w",
                name: "Amy W.",
                skillLevel: "USTA 3.0",
                focusArea: "Sharpening quick net reactions",
                joinedLabel: "Joined 2 days ago",
              },
            ],
          },
          {
            id: "2025-10-31-private-1730",
            time: "5:30 PM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 2,
          },
        ],
      },
      {
        id: "2025-11-01",
        day: "Sat",
        date: "01",
        label: "Nov 1",
        totalSlots: 14,
        slots: [
          {
            id: "2025-11-01-group-0830",
            time: "8:30 AM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 2,
            title: "Doubles Drills",
            participants: [
              {
                id: "liam-p",
                name: "Liam P.",
                skillLevel: "USTA 3.5",
                focusArea: "Looking for smarter poaches",
                joinedLabel: "Joined yesterday",
              },
              {
                id: "serena-k",
                name: "Serena K.",
                skillLevel: "USTA 3.0",
                focusArea: "Dialing in second serves",
                joinedLabel: "Joined 3 days ago",
              },
            ],
          },
          {
            id: "2025-11-01-private-1100",
            time: "11:00 AM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-11-01-private-1630",
            time: "4:30 PM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 2,
          },
        ],
      },
      {
        id: "2025-11-02",
        day: "Sun",
        date: "02",
        label: "Nov 2",
        totalSlots: 18,
        slots: [
          {
            id: "2025-11-02-private-0700",
            time: "7:00 AM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-11-02-group-0930",
            time: "9:30 AM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 4,
            title: "Intermediate Rally Squad",
          },
          {
            id: "2025-11-02-group-1500",
            time: "3:00 PM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 3,
            title: "Match Play Mixer",
            participants: [
              {
                id: "marco-t",
                name: "Marco T.",
                skillLevel: "USTA 3.5",
                focusArea: "Building attacking patterns",
                joinedLabel: "Joined today",
              },
            ],
          },
        ],
      },
      {
        id: "2025-11-03",
        day: "Mon",
        date: "03",
        label: "Nov 3",
        totalSlots: 12,
        slots: [
          {
            id: "2025-11-03-private-0630",
            time: "6:30 AM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-11-03-private-1200",
            time: "12:00 PM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-11-03-group-1830",
            time: "6:30 PM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 2,
            title: "Evening Cardio Tennis",
            participants: [
              {
                id: "jade-r",
                name: "Jade R.",
                skillLevel: "USTA 3.0",
                focusArea: "Boosting endurance for leagues",
                joinedLabel: "Joined 5 days ago",
              },
              {
                id: "mason-h",
                name: "Mason H.",
                skillLevel: "USTA 2.5",
                focusArea: "Improving rally consistency",
                joinedLabel: "Joined yesterday",
              },
            ],
          },
        ],
      },
      {
        id: "2025-11-04",
        day: "Tue",
        date: "04",
        label: "Nov 4",
        totalSlots: 16,
        slots: [
          {
            id: "2025-11-04-group-0800",
            time: "8:00 AM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 4,
            title: "Morning Rally Charge",
            participants: [],
          },
          {
            id: "2025-11-04-private-1330",
            time: "1:30 PM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-11-04-private-1900",
            time: "7:00 PM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 2,
          },
        ],
      },
      {
        id: "2025-11-05",
        day: "Wed",
        date: "05",
        label: "Nov 5",
        totalSlots: 15,
        slots: [
          {
            id: "2025-11-05-private-0700",
            time: "7:00 AM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-11-05-group-1000",
            time: "10:00 AM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 3,
            title: "Topspin Tune-Up",
            participants: [
              {
                id: "nina-s",
                name: "Nina S.",
                skillLevel: "USTA 3.0",
                focusArea: "Generating heavier topspin",
                joinedLabel: "Joined 4 days ago",
              },
            ],
          },
          {
            id: "2025-11-05-group-1700",
            time: "5:00 PM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 2,
            title: "Advanced Point Patterns",
            participants: [
              {
                id: "owen-l",
                name: "Owen L.",
                skillLevel: "USTA 4.0",
                focusArea: "Attacking short balls",
                joinedLabel: "Joined 1 week ago",
              },
              {
                id: "zoe-d",
                name: "Zoe D.",
                skillLevel: "USTA 3.5",
                focusArea: "Closing at the net",
                joinedLabel: "Joined 2 days ago",
              },
            ],
          },
        ],
      },
      {
        id: "2025-11-06",
        day: "Thu",
        date: "06",
        label: "Nov 6",
        totalSlots: 13,
        slots: [
          {
            id: "2025-11-06-group-0830",
            time: "8:30 AM",
            lessonType: "group",
            duration: "75 min",
            price: "$50",
            spotsRemaining: 4,
            title: "Breakfast Club Rally",
            participants: [],
          },
          {
            id: "2025-11-06-private-1100",
            time: "11:00 AM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 1,
          },
          {
            id: "2025-11-06-private-1800",
            time: "6:00 PM",
            lessonType: "private",
            duration: "60 min",
            price: "$85",
            spotsRemaining: 2,
          },
        ],
      },
    ],
    note: "Need a different time? Message Coach",
  },
};

export const mockCoachProfiles: CoachProfile[] = [
  {
    ...mockCoaches[0],
    headlineBadge: "Top Rated",
    about: sharedProfileBase.summary,
    highlightChips: sharedProfileBase.highlightChips,
    metrics: sharedProfileBase.metrics,
    certifications: sharedProfileBase.certifications,
    specialties: sharedProfileBase.specialties,
    coachingLocations: sharedProfileBase.coachingLocations,
    lessonDetails: sharedProfileBase.lessonDetails,
    lessonPackages: sharedProfileBase.lessonPackages,
    booking: sharedProfileBase.booking,
    levels: ["Beginner", "Intermediate", "Advanced", "Competitive"],
  },
  {
    ...mockCoaches[1],
    headlineBadge: "Player Favorite",
    about: sharedProfileBase.summary,
    highlightChips: [
      { icon: "users", label: "62 students" },
      { icon: "trophy", label: "Former D1 captain" },
      { icon: "spark", label: "UTR 9.3" },
    ],
    metrics: [
      { icon: "dollar", label: "Private", value: "$75", caption: "per session" },
      { icon: "users", label: "Group", value: "$55", caption: "per player" },
      { icon: "clock", label: "Response time", value: "2 hours" },
      { icon: "map", label: "Locations", value: "2" },
    ],
    certifications: ["LTA Level 4", "PTR Professional"],
    specialties: ["Topspin clinic", "Singles tactics", "Match analytics"],
    coachingLocations: ["Vista Courts", "Coastal Racquet Club"],
    lessonDetails: [
      {
        title: "Private session",
        description: "Technique deep dives using slow-motion capture and feedback loops.",
        price: "$75",
        cadence: "per hour",
      },
      {
        title: "Match play session",
        description: "Situational sets with in-point strategy adjustments and stats tracking.",
        price: "$55",
        cadence: "per player",
      },
    ],
    lessonPackages: [
      {
        lessonTypeId: "private" as const,
        id: "private-5-pack",
        title: "5 lesson package",
        lessons: 5,
        discount: "Save 10%",
        description: "Bundle private coaching and keep consistent weekly reps.",
        totalPrice: "$337.50",
        pricePerLesson: "$67.50 per lesson",
      },
      {
        lessonTypeId: "private" as const,
        id: "private-10-pack",
        title: "10 lesson package",
        lessons: 10,
        discount: "Save 15%",
        description: "Season-long training block with match analysis bonuses.",
        totalPrice: "$637.50",
        pricePerLesson: "$63.75 per lesson",
      },
      {
        lessonTypeId: "group" as const,
        id: "group-5-pack",
        title: "5 session package",
        lessons: 5,
        discount: "Save 10%",
        description: "Keep the team locked in with recurring group match play.",
        totalPrice: "$247.50",
        pricePerLesson: "$49.50 per player",
      },
      {
        lessonTypeId: "group" as const,
        id: "group-10-pack",
        title: "10 session package",
        lessons: 10,
        discount: "Save 15%",
        description: "Commit to a full season of shared reps and shared wins.",
        totalPrice: "$467.50",
        pricePerLesson: "$46.75 per player",
      },
    ],
    playerLessonCredits: [
      {
        lessonTypeId: "private",
        lessonTypeLabel: "Private lessons",
        remaining: 0,
        totalPurchased: 0,
        lastPurchasedLabel: "Starter pack available",
      },
      {
        lessonTypeId: "group",
        lessonTypeLabel: "Group sessions",
        remaining: 3,
        totalPurchased: 6,
        upcomingExpiryLabel: "3 credits expire July 2",
      },
    ],
    booking: {
      ...sharedProfileBase.booking,
      lessonTypes: [
        {
          id: "private",
          label: "Private lesson",
          price: "$75",
          unit: "/ hour",
          description: "1-on-1 coaching session",
          duration: "60 min • 1 player",
          tagline: "Detailed technical diagnostics",
          bullets: [
            "Video capture + swing notes",
            "Pattern-based situational drills",
            "Personalized practice tracker",
          ],
        },
        {
          id: "group",
          label: "Group session",
          price: "$55",
          unit: "/ player",
          description: "Point play focus",
          duration: "75 min • 3-4 players",
          tagline: "Pressure reps & tactical cues",
          bullets: [
            "Serve + first ball frameworks",
            "Team strategy scrimmages",
            "Shared progress summary",
          ],
        },
      ],
      defaultLessonType: "private",
      availableDates: [
        {
          id: "2025-10-29",
          day: "Wed",
          date: "29",
          label: "Oct 29",
          totalSlots: 12,
          slots: [
            {
              id: "2025-10-29-private-0800",
              time: "8:00 AM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 1,
            },
            {
              id: "2025-10-29-group-0930",
              time: "9:30 AM",
              lessonType: "group",
              duration: "75 min",
              price: "$55",
              spotsRemaining: 3,
              title: "Baseline Ball Machine",
              participants: [
                {
                  id: "harper-j",
                  name: "Harper J.",
                  skillLevel: "USTA 3.5",
                  focusArea: "Grooving deeper rally balls",
                  joinedLabel: "Joined 3 days ago",
                },
              ],
            },
            {
              id: "2025-10-29-private-1700",
              time: "5:00 PM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 2,
            },
          ],
        },
        {
          id: "2025-10-30",
          day: "Thu",
          date: "30",
          label: "Oct 30",
          totalSlots: 11,
          slots: [
            {
              id: "2025-10-30-group-0700",
              time: "7:00 AM",
              lessonType: "group",
              duration: "75 min",
              price: "$55",
              spotsRemaining: 4,
              title: "Sunrise Strategy Lab",
              participants: [],
            },
            {
              id: "2025-10-30-private-1130",
              time: "11:30 AM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 1,
            },
            {
              id: "2025-10-30-private-1800",
              time: "6:00 PM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 1,
            },
          ],
        },
        {
          id: "2025-11-01",
          day: "Sat",
          date: "01",
          label: "Nov 1",
          totalSlots: 14,
          slots: [
            {
              id: "2025-11-01-group-0830-b",
              time: "8:30 AM",
              lessonType: "group",
              duration: "75 min",
              price: "$55",
              spotsRemaining: 3,
              title: "Junior Power Hour",
              participants: [
                {
                  id: "eva-m",
                  name: "Eva M.",
                  skillLevel: "USTA 3.0",
                  focusArea: "Adding pace to forehand",
                  joinedLabel: "Joined yesterday",
                },
              ],
            },
            {
              id: "2025-11-01-private-1030",
              time: "10:30 AM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 1,
            },
            {
              id: "2025-11-01-group-1600",
              time: "4:00 PM",
              lessonType: "group",
              duration: "75 min",
              price: "$55",
              spotsRemaining: 2,
              title: "Weekend Match Mixer",
              participants: [
                {
                  id: "jackson-r",
                  name: "Jackson R.",
                  skillLevel: "USTA 4.0",
                  focusArea: "Transitioning to the net",
                  joinedLabel: "Joined today",
                },
                {
                  id: "olivia-s",
                  name: "Olivia S.",
                  skillLevel: "USTA 3.5",
                  focusArea: "Sharpening return games",
                  joinedLabel: "Joined 2 days ago",
                },
              ],
            },
          ],
        },
        {
          id: "2025-11-03",
          day: "Mon",
          date: "03",
          label: "Nov 3",
          totalSlots: 10,
          slots: [
            {
              id: "2025-11-03-private-0700-b",
              time: "7:00 AM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 1,
            },
            {
              id: "2025-11-03-group-1200",
              time: "12:00 PM",
              lessonType: "group",
              duration: "75 min",
              price: "$55",
              spotsRemaining: 2,
              title: "Lunch Break Live Ball",
              participants: [
                {
                  id: "sofia-l",
                  name: "Sofia L.",
                  skillLevel: "USTA 3.0",
                  focusArea: "Improving backhand depth",
                  joinedLabel: "Joined last week",
                },
                {
                  id: "daniel-g",
                  name: "Daniel G.",
                  skillLevel: "USTA 3.5",
                  focusArea: "Finishing at the net",
                  joinedLabel: "Joined 2 days ago",
                },
              ],
            },
            {
              id: "2025-11-03-private-1830",
              time: "6:30 PM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 1,
            },
          ],
        },
        {
          id: "2025-11-04",
          day: "Tue",
          date: "04",
          label: "Nov 4",
          totalSlots: 13,
          slots: [
            {
              id: "2025-11-04-group-0900",
              time: "9:00 AM",
              lessonType: "group",
              duration: "75 min",
              price: "$55",
              spotsRemaining: 4,
              title: "Footwork Foundations",
              participants: [],
            },
            {
              id: "2025-11-04-private-1400",
              time: "2:00 PM",
              lessonType: "private",
              duration: "60 min",
              price: "$75",
              spotsRemaining: 1,
            },
            {
              id: "2025-11-04-group-1730",
              time: "5:30 PM",
              lessonType: "group",
              duration: "75 min",
              price: "$55",
              spotsRemaining: 3,
              title: "Evening Point Play Lab",
              participants: [
                {
                  id: "mia-b",
                  name: "Mia B.",
                  skillLevel: "USTA 3.5",
                  focusArea: "Executing aggressive returns",
                  joinedLabel: "Joined yesterday",
                },
                {
                  id: "ethan-c",
                  name: "Ethan C.",
                  skillLevel: "USTA 4.0",
                  focusArea: "Sharpening mid-court volleys",
                  joinedLabel: "Joined 4 days ago",
                },
              ],
            },
          ],
        },
      ],
    },
  },
];

export const findCoachProfile = (id: string | number) => {
  const numericId = typeof id === "number" ? id : Number(id);
  if (Number.isNaN(numericId)) {
    return undefined;
  }
  return mockCoachProfiles.find((profile) => profile.id === numericId);
};
